import { useRef, useState } from 'react'
import { addCategory } from '@/data/categories'
import { usePaths } from '@/hooks/usePaths'
import { findByName, nextColor } from '@/logic/categories'
import { useCategories } from '@/store/categories'
import { fire } from '@/lib/writeError'
import type { Scope } from '@/types'
import { PlusIcon } from './icons'

interface NewCategoryChipProps {
  /** Where the category goes: shared for a shared task, private for a private one. */
  scope: Scope
  /** Called with the category to put on the task, new or one already of that name. */
  onPick: (categoryId: string) => void
}

/**
 * "+ New" at the end of the edit panel's category chips: type a name, Enter, and the category is made with
 * the next free colour and put on the task. Renaming and recolouring stay in Settings.
 */
export function NewCategoryChip({ scope, onPick }: NewCategoryChipProps) {
  const paths = usePaths()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  // Enter closes the field, and a removed field can also report a blur: create once per opening
  const handled = useRef(false)

  const create = async () => {
    if (handled.current) return
    handled.current = true
    const text = name.trim()
    setEditing(false)
    setName('')
    if (!text) return
    const { shared, private: priv } = useCategories.getState()
    const list = scope === 'shared' ? shared : priv
    const existing = findByName(list, text)
    if (existing) return onPick(existing.id)
    const id = await addCategory(paths, scope, text, nextColor([...shared, ...priv]), (list.at(-1)?.order ?? 0) + 1)
    onPick(id)
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="chip chip--pick chip--new"
        onClick={() => {
          handled.current = false
          setEditing(true)
        }}
      >
        <PlusIcon size={13} weight={2.4} />
        New
      </button>
    )
  }
  return (
    <span className="chip chip--pick chip--new-input">
      <label className="sr-only" htmlFor="category-new">
        New {scope === 'private' ? 'private ' : ''}category name
      </label>
      <input
        id="category-new"
        // opened by a tap on "+ New", so the cursor belongs here
        autoFocus
        type="text"
        maxLength={60}
        placeholder="Name"
        enterKeyHint="done"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            fire(create())
          } else if (e.key === 'Escape') {
            e.stopPropagation()
            handled.current = true
            setEditing(false)
            setName('')
          }
        }}
        onBlur={() => fire(create())}
      />
    </span>
  )
}
